import { createAxios, createDealerAxios } from 'src/factories/axios-factory';
import type { BespokeRate } from 'src/types/quote';
import { 
  DealerUserSettings, DealerGroupResponse, DealerInfo, DealerWebsiteConfig, 
  BoodilConfig, BoodilRegisterInterest, DealerSettings,
  NotificationSettings
} from 'src/types/dealer';
import { NotificationChannel_Enum } from 'src/types/enums';

const dealerBackendClient = createAxios(process.env.DealersApiUrl!);
const dealerBackendUser = createDealerAxios(process.env.DealersApiUrl!);

const dealerService = {
	getDealersByUserId(userId: number) {
		return new Promise<DealerInfo[]>(async (resolve, reject) => {
			dealerBackendClient
				.get(`/dealer/get-dealers-by-region?userId=${userId}`)
				.then(res => {
					resolve(res.data);
				})
				.catch(error => {
					console.log('no asm dealers');
				});
		});
	},
	getDealer(aid: number) {
		return new Promise<DealerInfo>(async (resolve, reject) => {
			dealerBackendClient
				.get(`/dealer/id?affiliateId=${aid}`)
				.then(res => {
					resolve(res.data);
				})
				.catch(error => {
					console.log('no dealers');
				});
		});
	},
	getDealers() {
		return new Promise<DealerInfo[]>(async (resolve, reject) => {
			dealerBackendClient
				.get(`/dealer/dealers`)
				.then(res => {
					resolve(res.data);
				})
				.catch(error => {
					console.log('no dealers');
				});
		});
	},
	getNonRegulatedDealers() {
		return new Promise<DealerInfo[]>(async (resolve, reject) => {
			dealerBackendClient
				.get(`/dealer/non-regulated`)
				.then(res => {
					resolve(res.data);
				})
				.catch(error => {
					console.log('no dealers');
				});
		});
	},
	getDealerGroup(affiliateUserId: number) {
		return new Promise<DealerGroupResponse | null>(async (resolve, reject) => {
			dealerBackendUser
				.get(`/dealer/group/${affiliateUserId}`)
				.then(res => {
					resolve(res.data as DealerGroupResponse);
				})
				.catch(error => {
					console.log('no dealer group');
					resolve(null);
				});
		});
	},
	getAllGroups() {
		return new Promise<DealerGroupResponse[] | null>(async (resolve, reject) => {
			dealerBackendUser
				.get(`/dealer/groups`)
				.then(res => {
					resolve(res.data as DealerGroupResponse[]);
				})
				.catch(error => {
					console.log('no dealer group');
					resolve(null);
				});
		});
	},
	getBespokeRates(affiliateUserId: number) {
		return new Promise<BespokeRate[]>(async (resolve, reject) => {
			// for dealers who do not want bespoke rates in portal
			if(affiliateUserId == 19979){
				resolve([]);
				return;
			}
			dealerBackendUser
				.get(`/rates/${affiliateUserId}`)
				.then(res => {
					resolve(res.data as BespokeRate[]);
				})
				.catch(error => {
					resolve([])
				});
		});
	},
	getUserSettings(dealerUserId: number) {
		return new Promise<DealerUserSettings>(async (resolve, reject) => {
			dealerBackendUser
				.get(`/dealer/user-settings/${dealerUserId}`)
				.then(res => {
					resolve(res.data as DealerUserSettings);
				})
				.catch(error => {
					console.log('no rates');
					reject(error);
				});
		});
	},
	updateUserSettings(userSettings: DealerUserSettings) {
		return new Promise<DealerUserSettings>(async (resolve, reject) => {
			dealerBackendUser
				.post(`/dealer/user-settings`, userSettings)
				.then(res => {
					resolve(res.data as DealerUserSettings);
				})
				.catch(error => {
					console.log('no rates');
					reject(error);
				});
		});
	},
	getNotificationSettings(affiliateUserId: number) {
		return new Promise<NotificationSettings>(async (resolve, reject) => {
			dealerBackendUser
				.get(`/dealer/notification-settings/${affiliateUserId}`)
				.then(res => {
					resolve(res.data as NotificationSettings);
				})
				.catch(error => {
					console.log('no rates');
					reject(error);
				});
		});
	},
	updateNotificationSettings(settings: NotificationSettings) {
		return new Promise<{ [key: string]: NotificationChannel_Enum[] }>(async (resolve, reject) => {
			dealerBackendUser
				.put(`/dealer/notification-settings`, settings)
				.then(res => {
					resolve(res.data as { [key: string]: NotificationChannel_Enum[] });
				})
				.catch(error => {
					console.log('no rates');
					reject(error);
				});
		});
	},
	getWebsiteConfig(dealerId: number) {
		return new Promise<DealerWebsiteConfig>(async (resolve, reject) => {
			dealerBackendUser
				.get(`/dealer/website-config/${dealerId}`)
				.then(res => {
					resolve(res.data as DealerWebsiteConfig);
				})
				.catch(error => {
					console.log('error getting website config');
					reject(error);
				});
		});
	},

	updateWebsiteConfig(config: DealerWebsiteConfig) {
		return new Promise<DealerWebsiteConfig>(async (resolve, reject) => {
			dealerBackendUser
				.post(`/dealer/website-config`, config)
				.then(res => {
					resolve(res.data as DealerWebsiteConfig);
				})
				.catch(error => {
					console.log('error updating website config');
					reject(error);
				});
		});
	},
	getBoodilConfig(dealerId: number, aid: string) {
		return new Promise<BoodilConfig>(async (resolve, reject) => {
			dealerBackendUser
				.get(`/boodil-config/${dealerId}?affiliateUserId=${aid}`)
				.then(res => {
					resolve(res.data as BoodilConfig);
				})
				.catch(error => {
					console.log('error getting website config');
					reject(error);
				});
		});
	},
	updateBoodilConfig(config: BoodilConfig) {
		return new Promise<BoodilConfig>(async (resolve, reject) => {
			dealerBackendUser
				.post(`/boodil-config`, config)
				.then(res => {
					resolve(res.data as BoodilConfig);
				})
				.catch(error => {
					console.log('error updating website config');
					reject(error);
				});
		});
	},
	registerBoodilInterest(config: BoodilRegisterInterest) {
		return new Promise<boolean>(async (resolve, reject) => {
			dealerBackendUser
				.post(`/boodil-register-interest`, config)
				.then(res => {
					resolve(res.data as boolean);
				})
				.catch(error => {
					console.log('error updating website config');
					reject(error);
				});
		});
	},
	updateDealerSettings(dealerSettings: DealerSettings) {        
		return new Promise<DealerSettings>(async (resolve, reject) => {
		  dealerBackendUser.post(`/dealer/dealer-settings`, dealerSettings).then(res => {
			resolve(res.data as DealerSettings);
		  }).catch(error => {
			console.log("error updating dealer settings");
			reject(error);
		  })
	
		})
	  },
	  getDealerSettings(affiliateId: number) {
		return new Promise<DealerSettings>(async (resolve, reject) => {
		  dealerBackendUser.get(`/dealer/settings/${affiliateId}`).then(res => {
			resolve(res.data as DealerSettings);
		  }).catch(error => {
			console.log("error getting dealer settings");
			reject(error);
		  })
	
		})
	  }
};

export default dealerService;
